import { z } from 'zod';

/**
 * The asset writes: creating a folder, uploading a file, granting access, deleting.
 *
 * **Two systems, and neither is a source of truth on its own.** S3 holds the bytes and
 * this database holds the row that names them; a row without its object is a broken
 * download and an object without its row is an invisible cost. Every shape here exists
 * to make that pairing checkable, which is why an upload is two calls rather than one
 * and why the client is never trusted for the size.
 *
 * Shared rather than duplicated because both ends genuinely need the same values: the
 * file picker's `accept` list and the server's rejection have to be the same list, or
 * a partner picks a file the server then refuses.
 */

/** Extensions the portal accepts. Brand material, not arbitrary uploads. */
export const ALLOWED_EXTENSIONS = [
  'ai',
  'csv',
  'doc',
  'docx',
  'eps',
  'gif',
  'jpeg',
  'jpg',
  'mov',
  'mp4',
  'pdf',
  'png',
  'ppt',
  'pptx',
  'psd',
  'svg',
  'txt',
  'webp',
  'xls',
  'xlsx',
  'zip',
] as const;

export type AllowedExtension = (typeof ALLOWED_EXTENSIONS)[number];

/** The `accept` attribute for a file input, e.g. ".png,.jpg,…". */
export function acceptAttribute(): string {
  return ALLOWED_EXTENSIONS.map((extension) => `.${extension}`).join(',');
}

/** The lowercase extension without the dot, or null when there is not one. */
export function extensionOf(filename: string): string | null {
  const match = /\.([A-Za-z0-9]{1,12})$/.exec(filename.trim());
  return match?.[1] ? match[1].toLowerCase() : null;
}

export function isAllowedExtension(extension: string | null): extension is AllowedExtension {
  return extension !== null && (ALLOWED_EXTENSIONS as readonly string[]).includes(extension);
}

// ---------------------------------------------------------------- folders

export const createFolderInputSchema = z.object({
  /**
   * 191 is the `name` column, which is `@unique` — MySQL's index limit at utf8mb4 is
   * what fixes it there, so the schema and the column agree by construction.
   */
  name: z.string().trim().min(1, 'A folder needs a name.').max(191),
  /**
   * Public folders are the shared brand library every partner sees. A private folder
   * starts visible to nobody and reaches a partner only through a grant — which is the
   * safe default: a folder created private and forgotten leaks nothing.
   */
  isPublic: z.boolean().default(false),
});
export type CreateFolderInput = z.infer<typeof createFolderInputSchema>;

/**
 * A folder as just created.
 *
 * Deliberately not the full `assetFolderSchema`: a new folder has no files and no
 * grants, and returning the read shape would imply the client could use it in place of
 * a refetch — which would be wrong the moment anything else changed.
 */
export const createdFolderSchema = z.object({
  id: z.string(),
  name: z.string(),
  isPublic: z.boolean(),
});
export type CreatedFolder = z.infer<typeof createdFolderSchema>;

export const grantFolderInputSchema = z.object({
  partnerId: z.string().min(1),
});
export type GrantFolderInput = z.infer<typeof grantFolderInputSchema>;

// ---------------------------------------------------------------- uploads

export const uploadIntentInputSchema = z.object({
  filename: z.string().trim().min(1, 'A file needs a name.').max(255),
  /**
   * The browser's `File.type`.
   *
   * Signed into the upload policy, so the file that arrives must declare the same
   * thing. Not trusted as a fact about the CONTENT — nothing here reads the bytes — and
   * it is why downloads are always served as an attachment.
   */
  contentType: z
    .string()
    .trim()
    .min(1)
    .max(191)
    .regex(/^[\w.+-]+\/[\w.+-]+$/, 'Not a media type.')
    .default('application/octet-stream'),
  /**
   * What the client believes the file weighs.
   *
   * Used to refuse an over-limit upload BEFORE a ticket is issued, and signed into the
   * policy so the store enforces it too. The size that ends up in the database comes
   * from the store afterwards, never from here.
   */
  sizeBytes: z.number().int().positive('An empty file has nothing to upload.'),
});
export type UploadIntentInput = z.infer<typeof uploadIntentInputSchema>;

export const uploadTicketSchema = z.object({
  /** Where the browser POSTs the file — the bucket, not this API. */
  url: z.string(),
  /** Policy, signature and the rest. Sent verbatim as form fields, in this order. */
  fields: z.record(z.string(), z.string()),
  /** The key the object will land at. Returned to confirm with. */
  key: z.string(),
  expiresAt: z.string(),
  /** Echoed so the page can show the ceiling it is working to. */
  maxBytes: z.number().int().positive(),
});
export type UploadTicket = z.infer<typeof uploadTicketSchema>;

export const confirmUploadInputSchema = z.object({
  /** The key from the ticket. Checked against the folder before a row is written. */
  key: z.string().min(1).max(1024),
  /** Shown under the filename. Free text, e.g. "Primary logo, transparent". */
  description: z.string().trim().max(191).optional(),
});
export type ConfirmUploadInput = z.infer<typeof confirmUploadInputSchema>;

export const downloadUrlSchema = z.object({
  url: z.string(),
  filename: z.string(),
  expiresAt: z.string(),
});
export type DownloadUrl = z.infer<typeof downloadUrlSchema>;

export const uploadedAssetSchema = z.object({
  folderId: z.string(),
  id: z.string(),
  name: z.string(),
  kind: z.string(),
  /** The size the STORE reported, which is the only one that is a fact. */
  sizeBytes: z.number().int(),
  updatedAt: z.string(),
});
export type UploadedAsset = z.infer<typeof uploadedAssetSchema>;
